import { Typography } from '@cube3/ui/src/typography/Typography';
import Button from '@cube3/ui/src/Buttons/Button';
import Surface from '@cube3/ui/src/Surface/Surface';
import React, { useCallback, useEffect, useState } from 'react';

export type StatusSeverity = 'info' | 'warning' | 'critical';

export interface StatusPayload {
  severity: StatusSeverity;
  message: string;
  ends_at?: string | null;
}

interface StatusNotificationProps {
  severity: StatusSeverity;
  message: string;
  ends_at?: string | Date | null;
  onSnooze?: () => void;
}

interface UseStatusConfig {
  interval?: number;
}

const SNOOZE_DURATION_MS: Record<StatusSeverity, number> = {
  info: 3 * 60 * 60 * 1000,
  warning: 60 * 60 * 1000,
  critical: 0
};

const isStatusPayload = (data: unknown): data is StatusPayload => {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    (d.severity === 'info' ||
      d.severity === 'warning' ||
      d.severity === 'critical') &&
    typeof d.message === 'string' &&
    (d.ends_at === undefined ||
      d.ends_at === null ||
      typeof d.ends_at === 'string')
  );
};

export const useStatus = ({
  interval = 5 * 60 * 1000
}: UseStatusConfig = {}) => {
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [snoozedUntil, setSnoozedUntil] = useState(0);

  const onHandleSnooze = useCallback(() => {
    if (!status || status.severity === 'critical') return;
    setSnoozedUntil(Date.now() + SNOOZE_DURATION_MS[status.severity]);
  }, [status]);

  useEffect(() => {
    const tick = () => {
      fetch('/api/v1/status')
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const next = isStatusPayload(data) ? data : null;
          setStatus((prev) => {
            const changed =
              !next ||
              prev?.message !== next.message ||
              prev?.severity !== next.severity ||
              prev?.ends_at !== next.ends_at;
            if (changed) setSnoozedUntil(0);
            return next;
          });
        })
        .catch((err) => {
          console.warn("Couldn't retrieve status", err);
        });
    };
    tick();
    const i = setInterval(tick, interval);
    return () => clearInterval(i);
  }, [interval]);

  const visible =
    status && (status.severity === 'critical' || Date.now() >= snoozedUntil)
      ? status
      : null;

  return {
    status: visible,
    snooze: onHandleSnooze,
    show: !!visible
  };
};

const severityStyles: Record<
  StatusSeverity,
  {
    background: 'base1' | 'base2';
    textColor: 'contrast1' | 'warning' | 'danger1';
  }
> = {
  info: { background: 'base2', textColor: 'contrast1' },
  warning: { background: 'base2', textColor: 'warning' },
  critical: { background: 'base1', textColor: 'danger1' }
};

const formatUntil = (ends_at: string | Date): string => {
  const date = typeof ends_at === 'string' ? new Date(ends_at) : ends_at;
  const now = new Date();
  const time = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  if (date.toDateString() === now.toDateString()) {
    return `Until ${time}`;
  }

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === tomorrow.toDateString()) {
    return `Until tomorrow ${time}`;
  }

  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' });
  return `Until ${weekday} ${time}`;
};

export const StatusNotification: React.FC<StatusNotificationProps> = ({
  severity,
  message,
  ends_at,
  onSnooze
}) => {
  const { background, textColor } = severityStyles[severity];
  const canSnooze = severity !== 'critical' && !!onSnooze;

  return (
    <Surface background={background} gradient={'lamp'} floating={true}>
      <div
        style={{
          display: 'flex',
          width: '100vw',
          minHeight: 64,
          alignItems: 'center',
          justifyContent: 'center',
          padding: 8,
          boxSizing: 'border-box'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
          <Typography variant="body1" color={textColor}>
            {message}
          </Typography>
          {ends_at && (
            <Typography variant="body1" color="contrast3">
              {formatUntil(ends_at)}
            </Typography>
          )}
        </div>
        {canSnooze && (
          <>
            <div style={{ width: '2em' }} />
            <Button onClick={onSnooze}>Snooze...</Button>
          </>
        )}
      </div>
    </Surface>
  );
};
